import { Point, type Coords } from "leaflet";
import type { Koordinaten, Polygon } from "../../types";

/** All tile coordinates covering the bounding box of `polygon` at the given zoom levels. */
export function getPrecacheCoords(
  polygon: Polygon,
  zooms: ReadonlyArray<number>,
): Coords[] {
  if (!polygon.length) return [];
  const latitudes = polygon
    .map((p) => p[0])
    .map((p) => (typeof p === "string" ? parseFloat(p) : p));
  const longitudes = polygon
    .map((p) => p[1])
    .map((p) => (typeof p === "string" ? parseFloat(p) : p));
  const nw = {
    latitude: Math.max(...latitudes),
    longitude: Math.min(...longitudes),
  };
  const se = {
    latitude: Math.min(...latitudes),
    longitude: Math.max(...longitudes),
  };
  return zooms.flatMap((zoom) => getPrecacheTiles(nw, se, zoom));
}

function getPrecacheTiles(
  nw: Koordinaten,
  se: Koordinaten,
  zoom: number,
): Coords[] {
  const topleft = coords2tile(nw, zoom);
  const bottomright = coords2tile(se, zoom);
  const xMin = Math.min(topleft.x, bottomright.x);
  const xMax = Math.max(topleft.x, bottomright.x);
  const yMin = Math.min(topleft.y, bottomright.y);
  const yMax = Math.max(topleft.y, bottomright.y);
  return range(xMin, xMax + 1).flatMap((x) =>
    range(yMin, yMax).map((y) => new CoordsPoint(x, y, zoom)),
  );
}

export function range(min: number, max: number): number[] {
  return Array.from(Array(max - min + 1).keys()).map((index) => index + min);
}

class CoordsPoint extends Point implements Coords {
  constructor(
    x: number,
    y: number,
    public readonly z: number,
  ) {
    super(x, y, false);
  }
}

type PointLike = {
  x: number;
  y: number;
};

function coords2tile(
  { latitude, longitude }: Koordinaten,
  zoom: number,
): PointLike {
  return { x: lng2tile(longitude, zoom), y: lat2tile(latitude, zoom) };
}

function lng2tile(lon: number, zoom: number) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
}

function lat2tile(lat: number, zoom: number) {
  return Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180),
      ) /
        Math.PI) /
      2) *
      Math.pow(2, zoom),
  );
}

/** Number of tiles getPrecacheCoords would return — computed without creating them. */
export function countPrecacheTiles(
  polygon: Polygon,
  zooms: ReadonlyArray<number>,
): number {
  if (!polygon.length) return 0;
  const latitudes = polygon.map((p) =>
    typeof p[0] === "string" ? parseFloat(p[0]) : p[0],
  );
  const longitudes = polygon.map((p) =>
    typeof p[1] === "string" ? parseFloat(p[1]) : p[1],
  );
  const nw = {
    latitude: Math.max(...latitudes),
    longitude: Math.min(...longitudes),
  };
  const se = {
    latitude: Math.min(...latitudes),
    longitude: Math.max(...longitudes),
  };
  return zooms.reduce((sum, zoom) => {
    const topleft = coords2tile(nw, zoom);
    const bottomright = coords2tile(se, zoom);
    const width = Math.abs(bottomright.x - topleft.x) + 2; // getPrecacheTiles includes xMax + 1
    const height = Math.abs(bottomright.y - topleft.y) + 1;
    return sum + width * height;
  }, 0);
}
